import React from "react";
import { useTranslation } from "react-i18next";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Bird, Upload, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { toast } from "sonner";
import { MASCOT_PRESETS, MascotSvg } from "@/components/FlappyMascots";

/**
 * FlappyEditor — admin/merchant config for the "Flappy Mascotte" game.
 * Lives in AdminGameEditor next to NinjaEditor / BlowCandlesEditor.
 *
 * Score thresholds + difficulty are admin-only to preserve fair-play
 * (merchants can still tweak mascot/obstacle/background via the merchant
 * editor in level-1 self-edit).
 */
export default function FlappyEditor({ game, onGameChange, isAdmin = true }) {
  const { t } = useTranslation();
  const set = (patch) => onGameChange(patch);
  const thresholds = game.flappy_thresholds || [5, 15, 30];

  async function uploadMascot(file) {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/uploads/mascot", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      set({ mascot_image: r.data.url, mascot_preset: null });
      toast.success(t("flappy.mascot_uploaded", "Mascotte téléversée"));
    } catch (e) {
      toast.error(t("flappy.mascot_upload_failed", "Échec du téléversement"));
    }
  }

  return (
    <div className="qrise-card p-6 space-y-5" data-testid="flappy-editor">
      <div className="flex items-center gap-2">
        <Bird className="h-4 w-4 text-violet-400" />
        <div className="text-sm font-semibold">
          {t("flappy.editor_title", "Configuration de la mascotte")}
        </div>
      </div>

      {/* ------- Mascot picker ------- */}
      <div>
        <Label className="text-xs font-medium">
          {t("flappy.editor_mascot", "Choisis ta mascotte")}
        </Label>
        <div className="mt-2 grid grid-cols-5 gap-2">
          {MASCOT_PRESETS.map((preset) => {
            const selected =
              !game.mascot_image && (game.mascot_preset || "cupcake") === preset.id;
            return (
              <button
                key={preset.id}
                type="button"
                onClick={() =>
                  set({ mascot_preset: preset.id, mascot_image: null })
                }
                className={`relative aspect-square rounded-xl border-2 transition flex items-center justify-center ${
                  selected
                    ? "border-violet-500 bg-violet-500/10 ring-2 ring-violet-500/30"
                    : "border-border hover:bg-muted/50"
                }`}
                data-testid={`flappy-mascot-${preset.id}`}
                aria-label={preset.label}
              >
                <MascotSvg preset={preset.id} size={42} />
              </button>
            );
          })}
        </div>
        <div className="mt-3 flex items-center gap-2">
          <label
            className="inline-flex items-center gap-1.5 cursor-pointer rounded-lg border border-border px-3 py-1.5 text-xs font-medium hover:bg-muted/50"
            data-testid="flappy-mascot-upload-btn"
          >
            <Upload className="h-3 w-3" />
            {t("flappy.upload_custom", "Uploader une image custom")}
            <input
              type="file"
              accept="image/png,image/jpeg,image/svg+xml,image/webp"
              className="hidden"
              onChange={(e) => uploadMascot(e.target.files?.[0])}
            />
          </label>
          {game.mascot_image && (
            <div className="flex items-center gap-2">
              <img
                src={game.mascot_image}
                alt="custom mascot"
                className="h-9 w-9 rounded-lg object-cover border border-border"
                data-testid="flappy-mascot-custom-preview"
              />
              <Button
                size="sm"
                variant="ghost"
                onClick={() => set({ mascot_image: null })}
                className="h-7 px-2 text-[11px]"
              >
                <X className="h-3 w-3 mr-1" />
                {t("common.remove", "Supprimer")}
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* ------- Themes ------- */}
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <Label className="text-xs font-medium">
            {t("flappy.editor_obstacle_theme", "Style des obstacles")}
          </Label>
          <Select
            value={game.obstacle_theme || "pipes"}
            onValueChange={(v) => set({ obstacle_theme: v })}
          >
            <SelectTrigger
              className="mt-1.5 rounded-xl h-10"
              data-testid="flappy-editor-obstacle"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="pipes">
                {t("flappy.obstacle_pipes", "Tuyaux classiques")}
              </SelectItem>
              <SelectItem value="forks">
                {t("flappy.obstacle_forks", "Fourchettes (resto)")}
              </SelectItem>
              <SelectItem value="cups">
                {t("flappy.obstacle_cups", "Gobelets (café)")}
              </SelectItem>
              <SelectItem value="books">
                {t("flappy.obstacle_books", "Livres (librairie)")}
              </SelectItem>
              <SelectItem value="bottles">
                {t("flappy.obstacle_bottles", "Bouteilles (bar)")}
              </SelectItem>
              <SelectItem value="buildings">
                {t("flappy.obstacle_buildings", "Gratte-ciels")}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs font-medium">
            {t("flappy.editor_background", "Ambiance du fond")}
          </Label>
          <Select
            value={game.background_theme || "noir"}
            onValueChange={(v) => set({ background_theme: v })}
          >
            <SelectTrigger
              className="mt-1.5 rounded-xl h-10"
              data-testid="flappy-editor-bg"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="noir">
                {t("flappy.bg_noir", "Holographic Noir (par défaut)")}
              </SelectItem>
              <SelectItem value="day">
                {t("flappy.bg_day", "Jour ensoleillé")}
              </SelectItem>
              <SelectItem value="night">
                {t("flappy.bg_night", "Nuit étoilée")}
              </SelectItem>
              <SelectItem value="sunset">
                {t("flappy.bg_sunset", "Coucher de soleil")}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* ------- Admin-only: difficulty + thresholds + duration ------- */}
      {isAdmin && (
        <>
          <div className="border-t border-border/60 pt-5">
            <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-muted-foreground mb-3">
              {t("flappy.editor_admin_section", "Réglages admin (équilibrage)")}
            </div>

            {/* Mode selector: tier (paliers) vs tournament */}
            <div className="mb-4">
              <Label className="text-xs font-medium">
                {t("flappy.editor_mode", "Mode de jeu")}
              </Label>
              <Select
                value={game.flappy_mode || "tier"}
                onValueChange={(v) => set({ flappy_mode: v })}
              >
                <SelectTrigger
                  className="mt-1.5 rounded-xl h-10"
                  data-testid="flappy-editor-mode"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="tier">
                    {t("flappy.mode_tier", "Paliers (gain immédiat)")}
                  </SelectItem>
                  <SelectItem value="tournament">
                    {t("flappy.mode_tournament", "Tournoi (classement, X gagnants)")}
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="mt-1.5 text-[11px] text-muted-foreground leading-snug">
                {(game.flappy_mode || "tier") === "tier"
                  ? t(
                      "flappy.mode_tier_hint",
                      "Le joueur gagne immédiatement selon son score. Les emails partent tout de suite.",
                    )
                  : t(
                      "flappy.mode_tournament_hint",
                      "Les X meilleurs scores à la fin du concours gagnent. Égalités départagées par le meilleur temps puis l'antériorité. Les emails partent automatiquement à la date de fin.",
                    )}
              </p>
            </div>

            {(game.flappy_mode || "tier") === "tournament" ? (
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label className="text-xs font-medium">
                    {t("flappy.editor_winners", "Nombre de gagnants")}
                  </Label>
                  <Input
                    type="number"
                    min={1}
                    max={100}
                    value={game.flappy_winners_count ?? 3}
                    onChange={(e) =>
                      set({
                        flappy_winners_count: Math.max(
                          1,
                          Math.min(100, Number(e.target.value) || 3),
                        ),
                      })
                    }
                    className="mt-1.5 rounded-xl"
                    data-testid="flappy-editor-winners"
                  />
                </div>
                <div>
                  <Label className="text-xs font-medium">
                    {t("flappy.editor_podium", "Attribution des lots")}
                  </Label>
                  <Select
                    value={game.flappy_podium ? "podium" : "flat"}
                    onValueChange={(v) => set({ flappy_podium: v === "podium" })}
                  >
                    <SelectTrigger
                      className="mt-1.5 rounded-xl h-10"
                      data-testid="flappy-editor-podium"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="flat">
                        {t("flappy.podium_flat", "Même lot pour tous les gagnants")}
                      </SelectItem>
                      <SelectItem value="podium">
                        {t("flappy.podium_on", "Podium (1er, 2e, 3e distincts)")}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            ) : null}

            <div className="mt-4 grid gap-4 sm:grid-cols-3">
              <div>
                <Label className="text-xs font-medium">
                  {t("flappy.editor_difficulty", "Difficulté")}
                </Label>
                <Select
                  value={game.flappy_difficulty || "normal"}
                  onValueChange={(v) => set({ flappy_difficulty: v })}
                >
                  <SelectTrigger
                    className="mt-1.5 rounded-xl h-10"
                    data-testid="flappy-editor-difficulty"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="easy">
                      {t("flappy.diff_easy", "Facile (gap large)")}
                    </SelectItem>
                    <SelectItem value="normal">
                      {t("flappy.diff_normal", "Normal")}
                    </SelectItem>
                    <SelectItem value="hard">
                      {t("flappy.diff_hard", "Difficile (gap serré)")}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-medium">
                  {t("flappy.editor_max_duration", "Durée max (s)")}
                </Label>
                <Input
                  type="number"
                  min={10}
                  max={120}
                  value={game.flappy_max_duration ?? 40}
                  onChange={(e) =>
                    set({
                      flappy_max_duration: Math.max(
                        10,
                        Math.min(120, Number(e.target.value) || 40),
                      ),
                    })
                  }
                  className="mt-1.5 rounded-xl"
                  data-testid="flappy-editor-duration"
                />
              </div>
            </div>

            {(game.flappy_mode || "tier") === "tier" && (
              <div className="mt-4">
                <Label className="text-xs font-medium">
                  {t("flappy.editor_thresholds", "Paliers de score (lot 1 / lot 2 / lot 3)")}
                </Label>
                <div className="mt-1.5 grid grid-cols-3 gap-2">
                  {[0, 1, 2].map((i) => (
                    <Input
                      key={i}
                      type="number"
                      min={1}
                      value={thresholds[i] ?? [5, 15, 30][i]}
                      onChange={(e) => {
                        const next = [...thresholds];
                        next[i] = Math.max(
                          1,
                          Number(e.target.value) || [5, 15, 30][i],
                        );
                        next.sort((a, b) => a - b);
                        set({ flappy_thresholds: next });
                      }}
                      className="rounded-xl"
                      data-testid={`flappy-editor-threshold-${i}`}
                    />
                  ))}
                </div>
                <p className="mt-1.5 text-[11px] text-muted-foreground leading-snug">
                  {t(
                    "flappy.editor_thresholds_hint",
                    "Score < palier 1 → perdant. Entre palier 1 et 2 → premier lot gagnant. Entre 2 et 3 → deuxième lot. ≥ palier 3 → jackpot.",
                  )}
                </p>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
